import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useSession } from '../lib/session';
import { useToast } from './Toast';
import './FeedbackPopup.css';

type Status = 'idle' | 'sending' | 'success' | 'error';

/** Chat-bubble glyph for the compact mobile bubble (desktop shows the label). */
function ContactIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
    </svg>
  );
}

const CATEGORIES = [
  { value: 'tech', label: 'Tech' },
  { value: 'feature', label: 'Feature Request' },
  { value: 'feedback', label: 'Feedback' },
  { value: 'idea', label: 'Idea' },
  { value: 'billing', label: 'Billing' },
  { value: 'other', label: 'Other' },
] as const;

type CategoryValue = (typeof CATEGORIES)[number]['value'];

export function FeedbackPopup() {
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState<CategoryValue | ''>('');
  const [body, setBody] = useState('');
  const [status, setStatus] = useState<Status>('idle');
  // Auto-hide the floating pill while scrolling DOWN on mobile (reveal on
  // scroll up), so it stops covering the content under it. Desktop ignores
  // this - the matchMedia gate keeps `hidden` false above the mobile width.
  const [hidden, setHidden] = useState(false);
  const { profile } = useSession();
  const toast = useToast();

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 640px)');
    let lastY = window.scrollY;
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        ticking = false;
        if (!mq.matches) { setHidden(false); return; }
        const y = window.scrollY;
        const dy = y - lastY;
        if (Math.abs(dy) < 6) return;
        // Hide when scrolling down (and past the top); show when scrolling up.
        setHidden(dy > 0 && y > 80);
        lastY = y;
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!body.trim() || !category || !profile) return;
    setStatus('sending');

    try {
      const { error } = await supabase.from('feedback').insert({
        user_id: profile.id,
        page_path: window.location.pathname,
        message: body.trim().slice(0, 4000),
        metadata: { category },
      });
      if (error) throw error;
      setStatus('success');
      setBody('');
      setCategory('');
      setTimeout(() => {
        setOpen(false);
        setStatus('idle');
        toast.success('Thanks, message received.');
      }, 1400);
    } catch (err) {
      console.error('[contact] submit failed', err);
      setStatus('error');
    }
  }

  return (
    <>
      <button
        type="button"
        className={`pill-action feedback-fab ${hidden ? 'feedback-fab--hidden' : ''}`}
        onClick={() => setOpen(true)}
        aria-label="Contact us"
      >
        <span className="feedback-fab__icon" aria-hidden="true"><ContactIcon /></span>
        <span className="feedback-fab__label">Contact</span>
      </button>

      {open && (
        <div className="feedback-modal" role="dialog" aria-modal="true" aria-labelledby="feedback-title">
          <div className="feedback-modal__scrim" onClick={() => setOpen(false)} />
          <form className="feedback-modal__panel" onSubmit={submit}>
            <header className="feedback-modal__head">
              <p className="kicker">Contact</p>
              <h2 id="feedback-title">Get in touch!</h2>
              <button
                type="button"
                className="feedback-modal__close"
                onClick={() => setOpen(false)}
                aria-label="Close"
              >
                ×
              </button>
            </header>

            <label className="feedback-modal__label" htmlFor="contact-category">
              Category
            </label>
            <div className="feedback-modal__select-wrap">
              <select
                id="contact-category"
                required
                value={category}
                onChange={(e) => setCategory(e.target.value as CategoryValue)}
                className="feedback-modal__select"
              >
                <option value="" disabled>
                  Pick one…
                </option>
                {CATEGORIES.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </select>
            </div>

            <label className="feedback-modal__label" htmlFor="contact-message">
              Message
            </label>
            <textarea
              id="contact-message"
              required
              placeholder="Tell us what's going on."
              maxLength={4000}
              value={body}
              onChange={(e) => setBody(e.target.value)}
            />

            <p className="feedback-modal__note">
              This goes straight to the team, and we love hearing from you!
            </p>

            <div className="feedback-modal__actions">
              <button
                type="submit"
                className="btn btn--primary btn--md"
                disabled={status === 'sending' || !body.trim() || !category}
              >
                {status === 'sending' ? 'Sending…' : 'Send'}
              </button>
            </div>

            {status === 'success' && <p className="feedback-modal__status feedback-modal__status--success">Thanks, message received.</p>}
            {status === 'error' && <p className="feedback-modal__status feedback-modal__status--error">Something broke. Try again.</p>}
          </form>
        </div>
      )}
    </>
  );
}
